import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { useWhatsAppSender } from "@/context/WhatsAppSendContext";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import InvoicePrint from "@/components/InvoicePrint";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  ArrowLeft, Printer, Download, MessageCircle, Mail, Wallet, FileCheck2, Truck,
  Pencil, Copy, Ban, FileText, Receipt, CheckCircle2, Link2, Link2Off,
} from "lucide-react";
import { toast } from "sonner";

export default function InvoiceDetail() {
  const requestWhatsAppSend = useWhatsAppSender();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const [inv, setInv] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [format, setFormat] = useState("a4");
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState(0);
  const [payMode, setPayMode] = useState("Cash");
  const [emailing, setEmailing] = useState(false);
  const fmtInit = useRef(false);

  const load = async () => {
    try { const { data } = await http.get(`/invoices/${id}`); setInv(data); setPayAmount(data.balance_due); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, "load invoice")); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (inv && new URLSearchParams(location.search).get("print")) setTimeout(() => window.print(), 500);
  }, [inv]);
  useEffect(() => {
    if (inv && !fmtInit.current) {
      fmtInit.current = true;
      const qf = new URLSearchParams(location.search).get("format");
      setFormat(qf || inv.company?.print_default_format || "a4");
    }
  }, [inv]);

  if (!inv) return <div className="p-8 space-y-3">
    <div className={loadError ? "text-rose-600" : "text-muted-foreground"}>{loadError || "Loading…"}</div>
    {loadError && <Button size="sm" variant="outline" onClick={load}>Retry</Button>}
  </div>;

  const recordPayment = async () => {
    if (!payAmount || payAmount <= 0) return toast.error("Enter amount");
    try {
      await http.post("/payments", { customer_id: inv.customer_id, invoice_id: inv.id, amount: +payAmount, payment_mode: payMode });
      toast.success("Payment recorded"); setPayOpen(false); load();
    } catch (e) { toast.error(requestErrorMessage(e, "record payment")); }
  };

  const einvoice = async () => {
    try { await http.post(`/invoices/${id}/einvoice`); toast.success("E-Invoice IRN generated"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "create e-invoice")); }
  };
  const eway = async () => {
    try { await http.post(`/invoices/${id}/eway`); toast.success("E-Way Bill generated"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "create e-way bill")); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this invoice?")) return;
    try { await http.post(`/invoices/${id}/cancel`); toast.success("Cancelled"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "cancel invoice")); }
  };
  const duplicate = async () => {
    try {
      const payload = { customer_id: inv.customer_id, place_of_supply: inv.place_of_supply, place_of_supply_code: inv.place_of_supply_code, items: inv.items, shipping: inv.shipping, other_charges: inv.other_charges, amount_received: 0 };
      const res = await http.post("/invoices", payload); toast.success("Duplicated"); navigate(`/sales/invoices/${res.data.id}`);
    } catch (e) { toast.error(requestErrorMessage(e, "duplicate invoice")); }
  };

  const shareMsg = `Hello ${inv.customer_name},\n\nPlease find details of Invoice ${inv.invoice_no} dated ${inv.invoice_date}.\nGrand Total: ${inr(inv.grand_total)}\nBalance Due: ${inr(inv.balance_due)}\n\nThank you,\n${inv.company?.name || ""}`;
  const ensureShare = async () => {
    const { data } = await http.post(`/invoices/${id}/share`);
    return `${window.location.origin}/p/invoice/${data.token}`;
  };
  const copyLink = async () => {
    try {
      const url = await ensureShare();
      try { if (navigator.clipboard) await navigator.clipboard.writeText(url); } catch { /* clipboard blocked; link still shown */ }
      toast.success("Public link copied", { description: url });
      load();
    } catch (e) { toast.error(requestErrorMessage(e, "create public invoice link")); }
  };
  const revokeLink = async () => {
    if (!window.confirm("Invalidate the public link? Anyone holding the old link will lose access.")) return;
    try { await http.delete(`/invoices/${id}/share`); toast.success("Public link invalidated"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "invalidate public invoice link")); }
  };
  const shareWhatsApp = async () => {
    const companyId = localStorage.getItem("vg_company");
    let url = "";
    try { url = await ensureShare(); await load(); }
    catch (e) { toast.warning(requestErrorMessage(e, "create share link")); }
    const msg = url ? `${shareMsg}\n\nView invoice: ${url}` : shareMsg;
    requestWhatsAppSend({ phone: inv.customer_phone, message: msg, title: `Invoice ${inv.invoice_no}`, companyId });
  };
  const shareEmail = async () => {
    if (!inv.customer_email) return toast.error("No email saved for this customer");
    setEmailing(true);
    try {
      const { data } = await http.post(`/invoices/${id}/email`);
      if (data.view_url && navigator.clipboard) { try { await navigator.clipboard.writeText(data.view_url); } catch {} }
      toast.success(`Invoice emailed to ${data.sent_to}`, { description: "Public link copied to clipboard" });
    } catch (e) { toast.error(requestErrorMessage(e, "email invoice")); }
    finally { setEmailing(false); }
  };

  const sourceChain = (inv.source_documents || []).map((s) => ({ label: s.label, no: s.no, done: true }));
  const chain = [
    ...sourceChain,
    { label: "Invoice", no: inv.invoice_no, done: true, active: true },
    { label: "Payment", no: inv.amount_received > 0 ? `${inr(inv.amount_received)}` : "", done: inv.amount_received > 0 },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {loadError && <div className="flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry refresh</Button></div>}
      <div className="no-print space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => navigate("/sales/invoices")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to invoices
          </button>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              {["a4", "a5", "thermal"].map((f) => (
                <button key={f} onClick={() => setFormat(f)} data-testid={`format-${f}`}
                  className={`px-3 py-1.5 text-xs font-medium uppercase ${format === f ? "bg-primary text-primary-foreground" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{f}</button>
              ))}
            </div>
            <Button size="sm" variant="print" onClick={() => window.print()} data-testid="print-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="pdf-button"><Download className="h-4 w-4 mr-1" /> PDF</Button>
          </div>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight font-mono">{inv.invoice_no}</h1>
              <StatusBadge status={inv.status} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">{inv.customer_name} · {inv.invoice_date}</p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Grand Total</div>
            <div className="font-heading text-2xl font-bold tabular">{inr(inv.grand_total)}</div>
          </div>
        </div>
        {inv.import_summary_only && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
            Historical summary only. Product lines, GST breakdown, payment date, and payment method were not in the export. The printout is not a tax invoice.
          </div>
        )}
        {!inv.import_summary_only && inv.import_stock_applied === false && (
          <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-950">
            Historical invoice with original product lines and GST. Existing receipts and balances are retained. Changes to this historical record do not adjust current stock.
          </div>
        )}

        {/* Document chain */}
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Document Chain</div>
          <div className="flex items-center gap-2 flex-wrap">
            {chain.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border capitalize ${s.active ? "bg-primary text-primary-foreground border-primary" : s.done ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                  {s.done && <CheckCircle2 className="h-3.5 w-3.5" />}{s.label}{s.no ? <span className="font-mono text-xs opacity-80">{s.no}</span> : null}
                </div>
                {i < chain.length - 1 && <div className="h-px w-5 bg-slate-300" />}
              </React.Fragment>
            ))}
          </div>
        </Card>

        {/* Action buttons */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setPayOpen(true)} data-testid="record-payment-button"><Wallet className="h-4 w-4 mr-1" /> Record Payment</Button>
            {!inv.import_summary_only && <>
              <Button size="sm" variant="outline" onClick={einvoice} data-testid="einvoice-button"><FileCheck2 className="h-4 w-4 mr-1" /> {inv.irn ? "E-Invoice ✓" : "Create E-Invoice"}</Button>
              <Button size="sm" variant="outline" onClick={eway} data-testid="eway-button"><Truck className="h-4 w-4 mr-1" /> {inv.eway_bill ? "E-Way ✓" : "Create E-Way Bill"}</Button>
              <Button size="sm" variant="outline" onClick={() => navigate(`/sales/credit-notes/new?invoice=${id}`)} data-testid="credit-note-button"><Receipt className="h-4 w-4 mr-1" /> Credit Note</Button>
              <Button size="sm" variant="outline" onClick={shareWhatsApp} data-testid="whatsapp-button"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button>
              <Button size="sm" variant="outline" onClick={shareEmail} disabled={emailing} data-testid="email-button"><Mail className="h-4 w-4 mr-1" /> {emailing ? "Sending…" : "Email"}</Button>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-link-button"><Link2 className="h-4 w-4 mr-1" /> Copy Link</Button>
            </>}
            {inv.has_share_link && <Button size="sm" variant="outline" className="text-rose-600" onClick={revokeLink} data-testid="revoke-link-button"><Link2Off className="h-4 w-4 mr-1" /> Invalidate Link</Button>}
            {!inv.import_summary_only && <>
              <Button size="sm" variant="outline" onClick={() => navigate(`/sales/invoices/${id}/edit`)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={duplicate}><Copy className="h-4 w-4 mr-1" /> Duplicate</Button>
            </>}
            {can("cancel_invoice") && <Button size="sm" variant="outline" className="text-rose-600" onClick={cancel}><Ban className="h-4 w-4 mr-1" /> Cancel</Button>}
          </div>
        </Card>

        {inv.payments?.length > 0 && (
          <Card className="p-5">
            <h3 className="font-heading font-semibold mb-3">Payments</h3>
            <div className="space-y-2">
              {inv.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm border-b last:border-0 pb-2">
                  <span className="font-mono">{p.payment_no}</span>
                  <span className="text-muted-foreground">{p.payment_date} · {p.payment_mode}</span>
                  <span className="font-mono text-emerald-600 font-semibold">{inr(p.amount)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Print preview */}
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Preview ({format})</div>
        <div className="border border-slate-200 rounded-lg overflow-auto bg-slate-100 p-4">
          <div className="shadow-lg mx-auto" style={{ width: "fit-content" }}>
             <InvoicePrint invoice={inv} format={format} printArea={false} />
          </div>
        </div>
      </div>

      {/* Hidden print target duplicate (visible only in print) */}
      <div className="hidden print:block">
         <InvoicePrint invoice={inv} format={format} printArea />
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment · {inv.invoice_no}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="text-sm flex justify-between"><span className="text-muted-foreground">Balance Due</span><span className="font-mono font-semibold">{inr(inv.balance_due)}</span></div>
            <div><Label>Amount</Label><Input type="number" value={payAmount} onChange={(e) => setPayAmount(+e.target.value)} className="mt-1.5" data-testid="pay-amount-input" /></div>
            <div>
              <Label>Payment Mode</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash", "Bank", "UPI", "Cheque", "Card", "NEFT"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={recordPayment} data-testid="confirm-payment-button">Save Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
